import React, { useState } from 'react';
import { useNavigate, Navigate, useSearchParams } from 'react-router-dom';
import { signIn, signUp } from '../services/authService';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';
import '../styles/auth.css';

// Same embedded logo as TopNavBar — no PNG dependency
const LogoMark = () => (
  <svg width="36" height="36" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M14 2 C14 2 15.2 8.8 18 11 C20.8 13.2 28 14 28 14 C28 14 20.8 14.8 18 17 C15.2 19.2 14 26 14 26 C14 26 12.8 19.2 10 17 C7.2 14.8 0 14 0 14 C0 14 7.2 13.2 10 11 C12.8 8.8 14 2 14 2Z"
      fill="rgba(255,255,255,0.92)"
    />
    <circle cx="14" cy="14" r="2.5" fill="rgba(139,38,53,0.4)" />
  </svg>
);

// Decorative circles for the left panel
const PanelDeco = () => (
  <svg className="auth-panel-deco" viewBox="0 0 400 600" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="350" cy="80"  r="180" stroke="rgba(255,255,255,0.08)" strokeWidth="1.5" fill="none"/>
    <circle cx="350" cy="80"  r="240" stroke="rgba(255,255,255,0.05)" strokeWidth="1.2" fill="none"/>
    <circle cx="60"  cy="520" r="160" stroke="rgba(255,255,255,0.07)" strokeWidth="1.5" fill="none"/>
    <circle cx="60"  cy="520" r="220" stroke="rgba(255,255,255,0.04)" strokeWidth="1"   fill="none"/>
    <circle cx="200" cy="300" r="90"  stroke="rgba(255,255,255,0.06)" strokeWidth="1.2" fill="none"/>
    <line x1="0"   y1="200" x2="400" y2="400" stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
    <line x1="0"   y1="350" x2="400" y2="550" stroke="rgba(255,255,255,0.04)" strokeWidth="1"/>
    <circle cx="320" cy="460" r="5" fill="rgba(255,255,255,0.2)"/>
    <circle cx="80"  cy="150" r="4" fill="rgba(255,255,255,0.18)"/>
    <circle cx="200" cy="300" r="3" fill="rgba(255,255,255,0.22)"/>
  </svg>
);

export const LoginPage = () => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const rawReturnTo = searchParams.get('returnTo') || '/dashboard';
  const returnTo = rawReturnTo.startsWith('/') && !rawReturnTo.startsWith('//') ? rawReturnTo : '/dashboard';

  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isSignUp) {
        const result = await signUp(email, password, displayName);
        if (result.success) { toast.success('Account created!'); navigate(returnTo, { replace: true }); }
        else toast.error(result.error);
      } else {
        const result = await signIn(email, password);
        if (result.success) { toast.success('Welcome back!'); navigate(returnTo, { replace: true }); }
        else toast.error(result.error);
      }
    } catch {
      toast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">

      {/* ── Left brand panel ── */}
      <div className="auth-panel">
        <PanelDeco />
        <div className="auth-panel-content">
          <div className="auth-panel-logo">
            <LogoMark />
            <span className="auth-panel-wordmark">
              <span className="auth-panel-wm1">Stimuli</span>
              <span className="auth-panel-wm2">Central</span>
            </span>
          </div>
          <h2 className="auth-panel-headline">
            Evidence-based stimulus training — built for real ABA therapy.
          </h2>
          <ul className="auth-panel-bullets">
            <li>Dozens of built-in stimulus decks</li>
            <li>Within-stimulus variation for generalization</li>
            <li>Automatic session tracking & data</li>
            <li>Custom decks with Pexels photo search</li>
          </ul>
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div className="auth-form-panel">
        <div className="auth-card">

          {/* mobile-only logo */}
          <div className="auth-mobile-logo">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
              <path d="M14 2 C14 2 15.2 8.8 18 11 C20.8 13.2 28 14 28 14 C28 14 20.8 14.8 18 17 C15.2 19.2 14 26 14 26 C14 26 12.8 19.2 10 17 C7.2 14.8 0 14 0 14 C0 14 7.2 13.2 10 11 C12.8 8.8 14 2 14 2Z" fill="#8B2635"/>
              <circle cx="14" cy="14" r="2.5" fill="rgba(255,255,255,0.55)"/>
            </svg>
            <span className="auth-mobile-wordmark">Stimuli Central</span>
          </div>

          <div className="auth-card-header">
            <h1>{isSignUp ? 'Create your account' : 'Welcome back'}</h1>
            <p>{isSignUp ? 'Start your 2-week free trial — full access from day one.' : 'Sign in to continue your sessions.'}</p>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            {isSignUp && (
              <div className="form-group">
                <label htmlFor="displayName">Full Name</label>
                <input id="displayName" type="text" value={displayName}
                  onChange={e => setDisplayName(e.target.value)} required placeholder="Your full name" />
              </div>
            )}
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input id="email" type="email" value={email}
                onChange={e => setEmail(e.target.value)} required placeholder="you@example.com" />
            </div>
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input id="password" type="password" value={password}
                onChange={e => setPassword(e.target.value)} required placeholder="••••••••" />
            </div>

            <button type="submit" disabled={loading} className="auth-button">
              {loading ? 'One moment…' : isSignUp ? 'Create Account →' : 'Sign In →'}
            </button>
          </form>

          <div className="auth-footer">
            <p>
              {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
              <button type="button" onClick={() => setIsSignUp(!isSignUp)} className="toggle-button">
                {isSignUp ? 'Sign In' : 'Sign Up Free'}
              </button>
            </p>
          </div>
        </div>
      </div>

    </div>
  );
};
